import { Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { api } from './_http';

type GetEventParams = {
	master_investment_pk?: number;
	event_pk?: string;
	master_pk?: string;
};
type GetEventDetailData = {
	event: EventData;
	event_take: EventTakeData[];
	event_sales_profit: EventSalesProfitData[];
	event_major_business: EventMajorBusinessData[];
	event_governance: EventGovernanceData[];
	my_bookmark: boolean;
};
type EventData = {
	master_pk: string;
	event_name: string;
	ticker: string;
	base_date: string;
	market: string;
	rise_rate_pk: number;
	bookmark: number;
	image: string;

	event_pk: number;
	event_code: string;
	file_pk: number;
	file_path: string;
	event_issued_stockcount: number;
	event_floating_stockcount: number;
	event_floating_ratio: string;
	event_price: number;
	event_company_outline: string;
	event_capital_changes: string;
	event_investment_indicators: string;
	event_investment_point_detail: string;
	event_enrollment_date: string;
	event_modifying_date: string;
	administrator_pk: number;
	master_investment_pk: number;
	master_investment_type: number;
	master_investment_score: number;
	master_investment_stability_score: number;
	master_investment_stability_description: string;
	master_investment_profitability_description: string;
	master_investment_profitability_score: number;
	master_investment_growth_description: string;
	master_investment_growth_score: number;
	master_investment_value_description: string;
	master_investment_value_score: number;
	master_investment_allocation_description: string;
	master_investment_allocation_score: number;
	master_investment_point_detail: null;
	master_investment_enrollment_date: string;
	master_investment_modifying_date: string;
	master_investment_standard_price: number;
	master_investment_standard_date: string;
	master_investment_per: string;
	master_investment_pbr: string;
	master_investment_market_cap: number;
	master_investment_category: string;
	master_investment_uploaded_yn: string;

	audio_file_name?: string;
	audio_file_path?: string;
};
type EventTakeData = {
	event_take_pk: number;
	event_take_year: string;
	event_take_amount: number;
	event_take_unit: string;
	event_pk: number;
};
type EventSalesProfitData = {
	event_sales_profit_pk: number;
	event_sales_profit_year: string;
	event_sales_profit_amount: number;
	event_sales_profit_unit: string;
	event_pk: number;
};
type EventMajorBusinessData = {
	event_major_business_pk: number;
	event_major_business_sector: string;
	event_major_business_share: string;
	event_major_business_unit: string;
	event_major_business_take: number;
	event_pk: number;
};
type EventGovernanceData = {
	event_governance_pk: number;
	event_governance_division: string;
	event_governance_stake: number;
	event_pk: number;
};
type GetFinanceParams = {
	event_pk: string;
	unit?: string;
};

export const useGetEventDetailApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getEventDetail';
	return useQuery<AxiosResponse, AxiosError, GetEventDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
	});
};
export type GetInvestReportOverviewRes = {
	invest_score: {
		score: string;
		price_dollar: string;
		price_won: string;
	};
	company_outline: string;
	top_box: {
		PER: string;
		PBR: string;
		배당률?: string;
		시가배당률?: string;
		ROE: string;
	};
	bottom_box: {
		PER: string;
		PBR: string;
		배당수익률: string;
		발행주식수: string;
		시가총액: string;
		ROE: string;
		유동주식수?: string;
		유동비율?: string;
		시가배당률?: string;
	};
};
export type GetInvestReportOverviewRes_KR = {
	invest_score: {
		score: string;
		price: string;
	};
	company_outline: string;
	report: string;
	top_box: {
		PER: string;
		PBR: string;
		배당률?: string;
		시가배당률?: string;
		ROE: string;
	};
	bottom_box: {
		PER: string;
		PBR: string;
		배당수익률: string;
		발행주식수: string;
		시가총액: string;
		ROE: string;
		유동주식수?: string;
		유동비율?: string;
		시가배당률?: string;
	};
	capital_change: {
		increase_status: number;
		outstanding_bw: number;
		outstanding_cb: number;
		reduction_status: number;
	};
};
export const useGetInvestReportOverviewApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getInvestReportOverview';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportOverviewRes>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

export type GetInvestReportScoreRes = {
	invest_score: {
		stability_description: string;
		stability_score: number;
		profitability_description: string;
		profitability_score: number;
		potential_description: string;
		potential_score: number;
		value_description: string;
		value_score: number;
		allocation_description: string;
		allocation_score: number;
		detail_description: string;
	};
};

export const useGetInvestReportScoreApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getInvestReportScore';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportScoreRes>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

type FinancialStatementItem = {
	매출액: string;
	매출원가: string;
	연구개발비: string;
	판매관리비: string;
	영업이익: string;
	영업외_손익: string;
	법인세: string;
	당기순이익: string;
	영업이익률: string;
	당기순이익률: string;
};

type FinancialStatementItem_KR = FinancialStatementItem & {
	매출총이익률: string;
};

type FinancialRatioItem = {
	매출액_증가율: string;
	영업이익_증가율: string;
	당기순이익_증가율: string;
	부채비율: string;
	당좌비율: string;
	유동비율: string;
	매출채권_회전일수: string;
	재고자산_회전일수: string;
};

type FinancialRatioItem_KR = FinancialRatioItem & {
	자본유보율: string;
};

type FinancialStatement = {
	연간: {
		[key: string]: FinancialStatementItem | FinancialStatementItem_KR;
	};
	분기: {
		[key: string]: FinancialStatementItem | FinancialRatioItem_KR;
	};
};

type FinancialRatio = {
	연간: {
		[key: string]: FinancialRatioItem;
	};
	분기: {
		[key: string]: FinancialRatioItem;
	};
};

type FinancialStatementData = {
	financial_statement: FinancialStatement;
	financial_ratio: FinancialRatio;
	total_count: string;
};

export const useGetFinancialStatementApi = (params: Ref<GetFinanceParams>) => {
	const url = '/v1/front/usa/getFinancialStatement';
	return useQuery<AxiosResponse, AxiosError, FinancialStatementData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		gcTime: 0,
		staleTime: 0,
		// enabled: false,
	});
};
type FinancialChartItem = {
	매출액_영업이익: string;
	영업이익률_당기순이익률: string;
	매출액_영업이익_증가율: string;
	유동비율_당좌비율: string;
	부채비율: string;
	PER과_EPS: string;
	PBR과_BPS: string;
	ROE과_ROIC: string;
	배당: string;
};
type FinancialChartData = {
	연간: { [key: string]: FinancialChartItem };
	분기: { [key: string]: FinancialChartItem };
	[key: string]: {
		[subKey: string]: FinancialChartItem;
	};
};

export const useGetFinancialChartApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getFinancialChart';
	return useQuery<AxiosResponse, AxiosError, FinancialChartData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};
type InvestmentIndicator = {
	'주당_순이익(EPS)': string;
	'주당_순자산(BPS)': string;
	'주가수익비율(PER)': string;
	'주가순자산비율(PBR)': string;
	'총자산_순이익률(ROA)': string;
	'자기자본_이익률(ROE)': string;
	'투하자본_이익률(ROIC)': string;
	'EV/EBITDA': string;
	'배당성향(%)': string;
	'연간_배당률(%)': string;
};

// 연간 지표 타입 정의 (날짜 key는 문자열로 설정)
export type AnnualInvestmentIndicator = {
	[date: string]: InvestmentIndicator; // 날짜를 문자열로 정의
};

// 분기 지표 타입 정의 (날짜 key는 문자열로 설정)
export type QuarterlyInvestmentIndicator = {
	[date: string]: InvestmentIndicator; // 날짜를 문자열로 정의
};

// 전체 투자 지표 타입 정의
type InvestIndicator = {
	연간: AnnualInvestmentIndicator;
	분기: QuarterlyInvestmentIndicator;
};

// 최상위 객체 타입 정의
type InvestIndicatorData = {
	invest_indicator: InvestIndicator;
};
export const useGetInvestIndicatorApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getInvestIndicator';
	return useQuery<AxiosResponse, AxiosError, InvestIndicatorData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		// enabled: false,
	});
};

type MainBusiness = {
	main_business_name: string;
	main_business_take: number;
	main_business_unit: string;
	main_business_share: number;
	main_business_description: string;
};

type MainCustomer = {
	main_customer_name: string;
	main_customer_take: number;
	main_customer_unit: string;
	main_customer_share: number;
};

export type InvestReportBusinessData = {
	main_business: {
		business: MainBusiness[];
		introduction: string;
	};
	main_customer: {
		customer: MainCustomer[];
		introduction: string;
	};
};

export const useGetInvestReportBusinessApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/usa/getInvestReportBusiness';
	return useQuery<AxiosResponse, AxiosError, InvestReportBusinessData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

// 한국주식
export const useGetEventDetailApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getEventDetail';
	return useQuery<AxiosResponse, AxiosError, GetEventDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
	});
};
export const useGetInvestReportOverviewApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getInvestReportOverview';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportOverviewRes_KR>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

export type GetInvestReportScoreRes_KR = {
	data: GetInvestReportScoreRes;
	error: {
		code: number;
	};
};
export const useGetInvestReportScoreApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getInvestReportScore';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportScoreRes_KR>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data,
		enabled: false,
	});
};

export const useGetFinancialStatementApi_KR = (params: Ref<GetFinanceParams>) => {
	const url = '/v1/front/getFinancialStatement';
	return useQuery<AxiosResponse, AxiosError, FinancialStatementData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		gcTime: 0,
		staleTime: 0,
		// enabled: false,
	});
};
export const useGetFinancialChartApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getFinancialChart';
	return useQuery<AxiosResponse, AxiosError, FinancialChartData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};
export const useGetInvestIndicatorApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getInvestIndicator';
	return useQuery<AxiosResponse, AxiosError, InvestIndicatorData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		// enabled: false,
	});
};

export type MainBusiness_KR = {
	event_major_business_pk: number;
	event_major_business_sector: string;
	event_major_business_sector_description: string;
	event_major_business_share: string;
	event_major_business_take: number;
	event_major_business_unit: string;
	event_pk: number;
};
export type GovernanceStructure = {
	event_governance_structure_client: string;
	event_governance_structure_pk: number;
	event_governance_structure_share: string;
	event_governance_structure_take: number;
	event_governance_structure_unit: string;
	event_pk: number;
};
export type InvestReportBusinessData_KR = {
	main_business: {
		business: MainBusiness_KR[];
		introduction: string;
	};
	event_governance_structure: {
		governance_structure: GovernanceStructure[];
		introduction: string;
	};
};
export const useGetInvestReportBusinessApi_KR = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getInvestReportBusiness';
	return useQuery<AxiosResponse, AxiosError, InvestReportBusinessData_KR>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};
export type InvestReportPointData = {
	event_issue: {
		event_issue_content: string;
		event_issue_date: string;
		event_issue_pk: number;
		event_pk: number;
	}[];
	event_disclosure: {
		event_disclosure_content: string;
		event_disclosure_date: string;
		event_disclosure_pk: number;
		event_pk: number;
	}[];
	invest_point: string;
};
export const useGetInvestReportPointApi = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/getInvestReportPoint';
	return useQuery<AxiosResponse, AxiosError, InvestReportPointData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

//------------------------------------------------------------------------------------
//일본주식 API Functions

export const useGetEventDetailApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getEventDetail';
	return useQuery<AxiosResponse, AxiosError, GetEventDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
	});
};

export const useGetInvestReportOverviewApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getInvestReportOverview';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportOverviewRes>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

export const useGetInvestReportScoreApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getInvestReportScore';
	return useQuery<AxiosResponse, AxiosError, GetInvestReportScoreRes>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

export const useGetFinancialStatementApi_JPN = (params: Ref<GetFinanceParams>) => {
	const url = '/v1/front/jpn/getFinancialStatement';
	return useQuery<AxiosResponse, AxiosError, FinancialStatementData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		gcTime: 0,
		staleTime: 0,
	});
};

export const useGetFinancialChartApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getFinancialChart';
	return useQuery<AxiosResponse, AxiosError, FinancialChartData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};

export const useGetInvestIndicatorApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getInvestIndicator';
	return useQuery<AxiosResponse, AxiosError, InvestIndicatorData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
	});
};

export const useGetInvestReportBusinessApi_JPN = (params: Ref<GetEventParams>) => {
	const url = '/v1/front/jpn/getInvestReportBusiness';
	return useQuery<AxiosResponse, AxiosError, InvestReportBusinessData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data?.data,
		enabled: false,
	});
};
